/**
 * Marketing parameter capture for Shopify orders.
 *
 * Captures UTM and ad-click parameters from the landing URL, persists them in
 * localStorage with first-touch precedence, and injects them as cart.attributes
 * on every cart mutation so they land in the order's note_attributes for BI.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'qst_mkt_params';
  var SESSION_SYNC_FLAG = 'qst_mkt_synced';
  var TTL_DAYS = 30;

  // First-touch params: never overwritten once captured.
  // Values stored raw — downstream BI (Gemini) parses the channel-specific
  // formats (e.g. pid=afl:<pid>:<cid>, kid=<channel>:<publisher>:<campaign>).
  var TRACKED = [
    // LD primary attribution codes (consumed by Magento, NAV, Adobe, Gemini).
    'pid', 'kid', 'eid', 'xid',
    // LD supplementary codes.
    'aid', 'sid', 'soc', 'tmd', 'vid',
    // Google Analytics UTMs (parallel scheme per the spec).
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    // Ad-platform click IDs — gclid/msclkid/efid are used directly in the
    // attribution SQL; the rest are kept for paid-channel measurement (Meta CAPI, etc.).
    'gclid', 'gad_source', 'gbraid', 'wbraid', 'efid',
    'fbclid', 'msclkid', 'ttclid', 'sccid', 'li_fat_id', 'irclickid',
    'mc_cid', 'mc_eid'
  ];

  function loadStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      var age = Date.now() - (parsed._stored_at || 0);
      if (age > TTL_DAYS * 86400000) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function save(data) {
    try {
      data._stored_at = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }

  // Search-engine referrer hostname fragments used to classify organic traffic.
  // Match M2's format: kid = 'seo:' + full referrer host (e.g. 'seo:www.google.com').
  var SEARCH_ENGINE_HOST_PATTERNS = [
    'google.', 'bing.', 'yahoo.', 'duckduckgo.', 'baidu.', 'yandex.',
    'ecosia.', 'startpage.', 'brave.com', 'ask.com', 'aol.'
  ];

  function inferOrganicKid() {
    var ref = document.referrer || '';
    var m = ref.match(/^https?:\/\/([^\/]+)/i);
    if (!m) return null;
    var host = m[1].toLowerCase();
    // Same-origin navigation is not organic.
    if (host === (window.location.hostname || '').toLowerCase()) return null;
    for (var i = 0; i < SEARCH_ENGINE_HOST_PATTERNS.length; i++) {
      if (host.indexOf(SEARCH_ENGINE_HOST_PATTERNS[i]) !== -1) {
        return 'seo:' + host;
      }
    }
    return null;
  }

  function captureFromURL() {
    var fresh = {};
    try {
      var params = new URLSearchParams(window.location.search);
      TRACKED.forEach(function (key) {
        var val = params.get(key);
        if (val) fresh[key] = val.slice(0, 255);
      });
    } catch (e) {}

    // If no explicit LD/ad-platform attribution came from the URL, infer organic
    // from the referrer. First-touch precedence in merge() prevents this from
    // overwriting a previously-captured paid/campaign source on later visits.
    var hasExplicit = fresh.pid || fresh.kid || fresh.eid || fresh.xid ||
      fresh.gclid || fresh.msclkid || fresh.fbclid || fresh.efid;
    if (!hasExplicit) {
      var organicKid = inferOrganicKid();
      if (organicKid) fresh.kid = organicKid;
    }

    return fresh;
  }

  function merge() {
    var stored = loadStored() || {};
    var fresh = captureFromURL();
    var hasFresh = Object.keys(fresh).length > 0;
    var merged = {};

    // First-touch wins for tracked params.
    TRACKED.forEach(function (key) {
      if (stored[key]) merged[key] = stored[key];
      else if (fresh[key]) merged[key] = fresh[key];
    });

    // Context fields.
    merged._first_landing_url = stored._first_landing_url || (hasFresh ? window.location.href : '');
    merged._first_referrer = stored._first_referrer || (hasFresh ? (document.referrer || '') : '');
    merged._first_seen_at = stored._first_seen_at || (hasFresh ? new Date().toISOString() : '');

    if (hasFresh) {
      merged._last_landing_url = window.location.href;
      merged._last_referrer = document.referrer || '';
      merged._last_seen_at = new Date().toISOString();
    } else {
      merged._last_landing_url = stored._last_landing_url || '';
      merged._last_referrer = stored._last_referrer || '';
      merged._last_seen_at = stored._last_seen_at || '';
    }

    // Only persist if we have at least one tracked value.
    var hasTrackedValue = TRACKED.some(function (k) { return merged[k]; });
    if (hasTrackedValue) save(merged);

    return merged;
  }

  function attributesPayload(data) {
    var attrs = {};
    TRACKED.forEach(function (k) {
      if (data[k]) attrs[k] = data[k];
    });
    if (data._first_landing_url) attrs.first_landing_url = data._first_landing_url;
    if (data._first_referrer) attrs.first_referrer = data._first_referrer;
    if (data._first_seen_at) attrs.first_seen_at = data._first_seen_at;
    return attrs;
  }

  function isCartMutation(url) {
    if (!url) return false;
    var s = String(url);
    // Match /cart/add.js, /cart/update.js, /cart/change.js (with or without .js)
    return /\/cart\/(add|update|change)(\.js)?(\?|$)/.test(s);
  }

  function injectIntoBody(body, attrs, headers) {
    var keys = Object.keys(attrs);
    if (!keys.length) return body;

    // No body — return JSON with just attributes.
    if (!body) {
      return JSON.stringify({ attributes: attrs });
    }

    // JSON string body.
    if (typeof body === 'string') {
      try {
        var parsed = JSON.parse(body);
        parsed.attributes = Object.assign({}, parsed.attributes || {}, attrs);
        return JSON.stringify(parsed);
      } catch (e) {
        // Treat as URL-encoded form.
        var params = new URLSearchParams(body);
        keys.forEach(function (k) {
          params.set('attributes[' + k + ']', attrs[k]);
        });
        return params.toString();
      }
    }

    // FormData body — mutate in place.
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      keys.forEach(function (k) {
        body.set('attributes[' + k + ']', attrs[k]);
      });
      return body;
    }

    // URLSearchParams body — mutate in place.
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      keys.forEach(function (k) {
        body.set('attributes[' + k + ']', attrs[k]);
      });
      return body;
    }

    // Unknown body type — leave it alone rather than risk breaking the request.
    return body;
  }

  var marketingData = merge();
  window.__qstMarketingParams = marketingData;

  // Patch fetch to inject attributes on every cart mutation.
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (isCartMutation(url)) {
          init = init || {};
          var attrs = attributesPayload(window.__qstMarketingParams || marketingData);
          if (Object.keys(attrs).length > 0) {
            init.body = injectIntoBody(init.body, attrs, init.headers);
          }
        }
      } catch (e) {
        // Never break the underlying request.
      }
      return origFetch.apply(this, [input, init]);
    };
  }

  // One-shot session sync: upsert attrs onto the cart even if the visitor
  // hasn't triggered an add/update yet. Throttled to once per session.
  function sessionSync() {
    if (!origFetch) return;
    try {
      if (sessionStorage.getItem(SESSION_SYNC_FLAG) === '1') return;
    } catch (e) {}

    var attrs = attributesPayload(marketingData);
    if (Object.keys(attrs).length === 0) return;

    origFetch.call(window, '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function () {
      try { sessionStorage.setItem(SESSION_SYNC_FLAG, '1'); } catch (e) {}
    }).catch(function () {});
  }

  // Defer the sync slightly so it doesn't compete with above-the-fold loading.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(sessionSync, 600); });
  } else {
    setTimeout(sessionSync, 600);
  }
})();
